import { apply, put, select, call } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import {
  login as loginAction,
  loginFailed,
  loginSucceeded,
  refreshTokenSucceeded,
  refreshTokenFailed,
  signupSucceeded,
  signupFailed,
  loadJobsSucceeded,
  loadJobsFailed,
  resetPasswordFailed,
  resetPasswordSucceeded,
  requestResetPasswordFailed,
  requestResetPasswordSucceeded,
  loadUserInfoSucceeded,
  onChangePasswordFailed,
  onChangePasswordSuccess,
  signupMultipleSucceeded,
  signupMultipleFailed,
  onLoadConfigurationSuccess,
  onLoadConfigurationFailed,
  loadUserRolesSuccessAction,
  loadUserGroupsForUserSuccessAction,
  checkOrganizationNameAvailableSuccessAction,
} from '../reducers/auth.reducers';
import { navigate } from '../reducers/navigator.reducers';
import { successNotification, errorNotification } from '../reducers/notification.reducers';
import Config from '../../run-config';
import publicRoutes from '../util/publicRoutes';

const errorMessages = {
  0: 'E-Mail wurde bereits vergeben',
  1: 'Passwörter stimmen nicht überein',
  2: 'Mindestens ein Kleinbuchstabe muss im Passwort vorkommen',
  3: 'Mindestens ein Großbuchstabe muss im Passwort vorkommen',
  4: 'Mindestens ein Sonderzeichen muss im Passwort vorkommen',
  5: 'Mindestens eine Zahl muss im Passwort vorkommen',
  6: 'Das Passwort ist zu kurz (mind. 6 Zeichen)',
  7: 'Es konnte kein Nutzer mit dieser E-Mail Adresse gefunden werden',
  11: 'Ungültiger Reset-Token!',
  12: 'Ungültiger Reset-Token!',
  13: 'E-Mail Domain ist nicht zulässig, Bitte verwenden Sie eine andere E-Mail Adresse',
  500: 'Email bereits vergeben.',
};
const loginErrorMessages = {
  invalid_grant: 'Login fehlgeschlagen!',
  emailNotActivated: 'E-Mail Adresse noch nicht aktiviert, erneute Aktivierungsmail wurde gesendet',
  lockedOut: 'Zu viele Anmeldungsversuche, probiere es später erneut',
  userLocked: 'Dieser Account wurde durch einen Administrator gesperrt',
  userDeleted: 'Dieser Account wurde von einem Administrator gelöscht',
};

const getErrorMessage = (payload) => {
  if (payload && errorMessages[payload.errorCode]) {
    return errorMessages[payload.errorCode];
  }
  return 'Ein Fehler ist aufgetreten!';
};

const getLoginErrorMessage = (payload) => {
  if (payload && payload.error && loginErrorMessages[payload.error]) {
    return loginErrorMessages[payload.error];
  }
  return 'Ein Fehler ist aufgetreten!';
};

const checkIsPublicRoute = (route) => {
  const routes = publicRoutes.map(r => r.toUpperCase());
  return routes.indexOf(route.toUpperCase() !== -1);
};

export function* login(authApi, { payload }) {
  try {
    const credentials = payload;
    const response = yield apply(
      authApi,
      authApi.login,
      [credentials.userName, credentials.password]);

    if (response.error) {
      yield put(loginFailed({
        message: getLoginErrorMessage(response),
      }));
    } else {
      yield put(loginSucceeded({
        refreshToken: response.refresh_token,
        accessToken: response.access_token,
        expiresIn: response.expires_in,
      }));
      yield put(navigate({
        route: '/courses',
      }));
    }
  } catch (e) {
    console.log(e);
    yield put(loginFailed({
      message: 'Ein Fehler ist aufgetreten!',
    }));
  }
}

export function* authorize(authApi, { payload }) {
  try {
    const response = yield apply(
      authApi,
      authApi.externalLogin,
      [payload]);
  } catch (e) {
  }
}

export function* externalLogin(authApi, { payload }) {
  try {
    const credentials = payload;
    const response = yield apply(
      authApi,
      authApi.login,
      [credentials.userName, credentials.password]);

    if (response.error) {
      yield put(loginFailed({
        message: getLoginErrorMessage(response),
      }));
    } else {
      yield put(loginSucceeded({
        refreshToken: response.refresh_token,
        accessToken: response.access_token,
        expiresIn: response.expires_in,
      }));
    }
  } catch (e) {
    yield put(loginFailed({
      message: 'Ein Fehler ist aufgetreten!',
    }));
  }
}

let isRefreshing = false;
export function* refreshAccessToken(authApi) {
  const currentRoute = yield select(state => state.navigator.route);
  try {
    const exit = isRefreshing;

    // do not execute refresh task twice
    while (isRefreshing) {
      yield call(delay, 500);
    }

    if (exit) {
      return;
    }

    // refresh token
    isRefreshing = true;
    const refreshToken = yield select(state => state.authentication.refreshToken);
    const response = yield apply(authApi, authApi.refreshAccessToken, [refreshToken]);

    if (response.error) {
      yield put(refreshTokenFailed({
        message: 'Session abgelaufen',
      }));

      if (currentRoute && !checkIsPublicRoute(currentRoute)) {
        yield put(navigate({
          route: '/login',
        }));
      }
    } else {
      yield put(refreshTokenSucceeded({
        refreshToken: response.refresh_token,
        accessToken: response.access_token,
        expiresIn: response.expires_in,
      }));
    }

    isRefreshing = false;
  } catch (e) {
    yield put(refreshTokenFailed({
      message: 'Session abgelaufen',
    }));

    if (currentRoute && !checkIsPublicRoute(currentRoute)) {
      yield put(navigate({
        route: '/login',
      }));
    }
  }
}

export function* checkOrganizationNameIsAvialable(userApi, { payload }) {
  try {
    const response = yield apply(
      userApi,
      userApi.checkOrganizationNameAvailable,
      [payload],
    );

    if (response.errorCode) {
      yield put(checkOrganizationNameAvailableSuccessAction(false));
    } else {
      yield put(checkOrganizationNameAvailableSuccessAction(response));
    }
  } catch (e) {
    // nothing to do
  }
}

export function* platformSignup(userApi, { payload }) {
  try {
    const response = yield apply(
      userApi,
      userApi.platformRegister,
      [payload]);

    if (response.errorCode != null) {
      yield put(signupFailed({
        message: getErrorMessage(response),
      }));
      return;
    }

    yield put(signupSucceeded());
    yield put(successNotification('Wir haben dir soeben eine E-Mail mit einem Aktivierungslink gesendet. Klicke auf diesen Aktivierungslink, um die Registrierung abzuschließen!'));
  } catch (e) {
    yield put(signupFailed({
      message: getErrorMessage(e),
    }));
  }
}

export function* signup(userApi, { payload }) {
  try {
    const response = yield apply(
      userApi,
      userApi.register,
      [payload]);
    if (response.errorCode != null) {
      yield put(signupFailed({
        message: getErrorMessage(response),
      }));
      return;
    }

    const { email, password } = payload;

    yield put(signupSucceeded());

    if (payload.brand) {
      yield put(navigate({
        route: `/login-${payload.brand}`,
      }));
    } else {
      yield put(navigate({
        route: '/login',
      }));
    }
    // yield put(loginAction({ userName: email, password }));

    yield put(successNotification('Wir haben dir soeben eine E-Mail mit einem Aktivierungslink gesendet. Klicke auf diesen Aktivierungslink, um die Registrierung abzuschließen!'));
  } catch (e) {
    yield put(signupFailed({
      message: getErrorMessage(e),
    }));
  }
}

export function* requestPasswordReset(userApi, { payload }) {
  try {
    const response = yield apply(
      userApi,
      userApi.requestPasswordReset,
      [payload],
    );

    if (response.errorCode != null) {
      yield put(requestResetPasswordFailed({
        message: getErrorMessage(response),
      }));
      return;
    }

    yield put(requestResetPasswordSucceeded());
    yield put(successNotification('Anfrage erfolgreich. Bitte überprüfen Sie Ihre E-Mails'));
    yield put(navigate({
      route: '/',
    }));
  } catch (e) {
    yield put(requestResetPasswordFailed({
      message: getErrorMessage(e),
    }));
  }
}

export function* resetPassword(userApi, { payload }) {
  try {
    const response = yield apply(
      userApi,
      userApi.resetPassword,
      [payload]);

    if (response.errorCode != null) {
      yield put(resetPasswordFailed({
        message: getErrorMessage(response),
      }));
      return;
    }

    yield put(resetPasswordSucceeded());
    yield put(successNotification('Ihr Passwort wurde geändert'));
    yield put(navigate({
      route: '/',
    }));
  } catch (e) {
    yield put(resetPasswordFailed({
      message: getErrorMessage(e),
    }));
  }
}

export function* loadJobs(userApi) {
  try {
    const response = yield apply(
      userApi,
      userApi.loadJobs);

    if (response.errorCode != null) {
      yield put(loadJobsFailed());
    }

    yield put(loadJobsSucceeded({ jobs: response }));
  } catch (e) {
    yield put(loadJobsFailed());
  }
}

export function* loadUserInfo(userApi) {
  const response = yield apply(userApi, userApi.loadInfo);
  if (response.errorCode) {
    // yield put(errorNotification('Fehler beim Laden der Benutzerinformationen'));
  } else {
    yield put(loadUserInfoSucceeded(response));
  }
}

export function* changeUserData(userApi, { payload }) {
  const response = yield apply(userApi, userApi.updateUserData, [payload]);
  if (response.errorCode) {
    yield put(onChangePasswordFailed('Leider ist beim aktuallisieren ein Fehler aufgetreten'));
  } else {
    yield put(loadUserInfoSucceeded(response));
    yield put(successNotification('Benutzerdaten wurden aktuallisiert'));
  }
}

export function* changeUserPassword(userApi, { payload }) {
  const response = yield apply(userApi, userApi.changePassword, [payload]);
  if (response.errorCode) {
    yield put(onChangePasswordFailed('Passwort ändern fehlgeschlagen. Überprüfen Sie Ihr altes Passwort'));
  } else {
    yield put(onChangePasswordSuccess());
    yield put(successNotification('Passwort erfolgreich geändert'));
  }
}

export function* registerMultiple(userApi, { payload }) {
  try {
    const response = yield apply(userApi, userApi.registerMultiple, [payload]);

    if (response.errorCode) {
      yield put(errorNotification('Leider ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut oder kontaktieren Sie einen Administrator'));
      yield put(signupMultipleFailed());
    } else {
      yield put(successNotification('Sammelanmeldung erfolgreich'));
      yield put(signupMultipleSucceeded());
      yield put(navigate({
        route: '/',
      }));
    }
  } catch (e) {
    yield put(errorNotification('Leider ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut oder kontaktieren Sie einen Administrator'));
    yield put(signupMultipleFailed());
  }
}

export function* loadConfigurationForBrand(appApi) {
  try {
    const response = yield apply(
      appApi,
      appApi.loadConfiguration, [Config.brand],
    );

    if (response.errorCode || !Array.isArray(response)) {
      yield put(onLoadConfigurationFailed());
    } else {
      const environment = {};

      response.forEach((e) => {
        environment[e.key] = e.value;
      });

      yield put(onLoadConfigurationSuccess(environment));
    }
  } catch (e) {
    yield put(onLoadConfigurationFailed());
  }
}

export function* loadUserRoles(authApi) {
  try {
    const response = yield apply(
      authApi,
      authApi.loadUserRoles);

    if (response.error) {
    } else {
      yield put(loadUserRolesSuccessAction(response));
    }
  } catch (e) {

  }
}

export function* loadUserGroups(userApi) {
  try {
    const response = yield apply(
      userApi,
      userApi.loadUserGroups);

    if (response.error) {

    } else {
      yield put(loadUserGroupsForUserSuccessAction(response));
    }
  } catch (e) {

  }
}



// WEBPACK FOOTER //
// ./src/app/sagas/auth.sagas.js